import {
  type RefObject,
  type UIEvent as ReactUIEvent,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import type { IAiSpreadsheetLoadedSheet } from "@/payroll/ai-script/types";
import {
  captureGridViewport,
  isGridScroller,
  scheduleGridViewportRestore,
  type GridViewportSnapshot,
} from "./grid-scroll";

export const useGridViewportPreservation = ({
  activeSheet,
  gridWrapperRef,
}: {
  activeSheet: IAiSpreadsheetLoadedSheet | null;
  gridWrapperRef: RefObject<HTMLDivElement | null>;
}) => {
  const activeSheetId = activeSheet?.id ?? null;
  const viewportSnapshotRef = useRef<GridViewportSnapshot | null>(null);
  const restoreCancelRef = useRef<(() => void) | null>(null);

  const cancelViewportRestore = useCallback(() => {
    restoreCancelRef.current?.();
    restoreCancelRef.current = null;
  }, []);

  const clearGridViewport = useCallback(() => {
    cancelViewportRestore();
    viewportSnapshotRef.current = null;
  }, [cancelViewportRestore]);

  const handleGridScrollCapture = useCallback(
    (event: ReactUIEvent<HTMLDivElement>) => {
      if (!activeSheetId) return;
      const scroller = event.target;
      if (!isGridScroller(scroller)) return;

      viewportSnapshotRef.current = captureGridViewport(
        activeSheetId,
        scroller,
      );
    },
    [activeSheetId],
  );

  // Child layout effects run before this parent hook, so Glide has completed
  // its data/layout reconciliation before we restore the native offset. Brief
  // re-assertions cover its asynchronous canvas and resize passes.
  useLayoutEffect(() => {
    if (!activeSheetId) return undefined;

    cancelViewportRestore();
    restoreCancelRef.current = scheduleGridViewportRestore({
      gridWrapper: gridWrapperRef.current,
      activeSheetId,
      snapshot: viewportSnapshotRef.current,
    });

    return cancelViewportRestore;
  }, [activeSheet, activeSheetId, cancelViewportRestore, gridWrapperRef]);

  return {
    cancelViewportRestore,
    clearGridViewport,
    handleGridScrollCapture,
  };
};
